/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MatchModule */

import { L10nService } from '@vmw/ngx-vip';

import {
    IPortMatchGeneric,
    MatchOperation,
} from 'generated-types';

import { PortMatchGeneric } from 'object-types';
import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { PortRangeConfigItem }
    from 'ajs/modules/policies/factories/config-items/port-range.config-item.factory';
import { TEnumValueLabelsHash } from 'ajs/modules/core/services/schema-service';

import * as l10n from '../match.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

interface IPortMatchGenericConfig
    extends Omit<IPortMatchGeneric, 'match_criteria' | 'ranges'>
{
    match_criteria?: MatchOperation;
    ranges?: RepeatedMessageItem<PortRangeConfigItem>;
}

/**
 * @desc PortMatchGeneric MessageItem class.
 * @author Abhinesh Gour
 */
export class PortMatchGenericConfigItem extends MessageItem<IPortMatchGenericConfig> {
    public static ajsDependencies = [
        'l10nService',
        'schemaService',
    ];

    /**
     * Used for parsing match criteria string for expander info.
     */
    private matchOperationLabelsHash: TEnumValueLabelsHash;

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: PortMatchGeneric,
            ...args,
        };

        super(extendedArgs);

        const schemaService = this.getAjsDependency_('schemaService');

        this.matchOperationLabelsHash = schemaService.getEnumValueLabelsHash('MatchOperation');
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IPortMatchGenericConfig> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const defaultConfig = defaultValues.getDefaultItemConfigByType(this.objectType) || {};

        return {
            ...defaultConfig,
            match_criteria: MatchOperation.IS_IN,
        };
    }

    /**
     * Returns the label for this.config.match_criteria.
     * Commonly used as part of expander info.
     */
    protected get matchCriteriaLabel(): string {
        const { match_criteria: matchCriteria } = this.config;

        const parsedMatchCriteria = this.matchOperationLabelsHash[matchCriteria]
            .split(' ')[0]
            .toLowerCase();

        return parsedMatchCriteria;
    }

    /**
     * Value of match to represent the data. Used as part of expander info.
     */
    public get matchValue(): string {
        const {
            ports,
            ranges: portRanges,
        } = this.config;

        const hasPorts = ports?.length > 0;
        const hasPortRanges = !portRanges?.isEmpty();

        let portsValue = '';

        if (hasPorts) {
            portsValue = ports.join(', ');
        }

        let portRangesValue = '';

        if (hasPortRanges) {
            const portRangeList = portRanges.config.map(
                (portRangeConfigItem: PortRangeConfigItem) => portRangeConfigItem.toString(),
            );

            portRangesValue = portRangeList.join(', ');
        }

        return [portsValue, portRangesValue].join(', ');
    }

    /**
     * Label of match. Commonly used as part of expander info.
     */
    public get matchLabel(): string {
        return this.l10nService.getMessage(l10nKeys.portMatchLabel);
    }

    /**
     * Parse flattened array of ports to stand-alone port list and port range data.
     */
    public updateDataWithPortList(portList: number[]): void {
        this.config.ports = [];
        this.config.ranges.removeAll();

        const { ports, ranges: portRanges } = this.config;
        const sortedPorts = portList.slice()
            .sort((a, b) => a - b); // To avoid alphabetic sort

        let readingRange = false;
        let start: number;

        // Handle up to the second to the last port number in the list
        for (let i = 0; i < sortedPorts.length - 1; i++) {
            const curr = sortedPorts[i];
            const next = sortedPorts[i + 1];

            // Next is a consecutive number of current
            if (curr + 1 === next) {
                if (!readingRange) {
                    start = curr;
                    readingRange = true;
                }
            // Next is not a consecutive number of current and reading range
            } else if (readingRange) {
                const end = curr;

                readingRange = false;

                portRanges.add({
                    start,
                    end,
                });
            // Next is not a consecutive number of current and not reading range
            // meaning current is a stand-alone number
            } else {
                ports.push(curr);
            }
        }

        // Handle the last port number in the list
        const last = sortedPorts[sortedPorts.length - 1];

        if (last) {
            if (readingRange) {
                const end = last;

                portRanges.add({
                    start,
                    end,
                });
            } else {
                ports.push(last);
            }
        }
    }

    /** @override */
    public toString(): string {
        return `${this.matchCriteriaLabel} ${this.matchValue}`;
    }

    /** @override */
    protected modifyConfigDataBeforeSave(): void {
        const { ports } = this.config;

        if (ports && ports.length === 0) {
            delete this.config.ports;
        }
    }
}
